import Image from "next/legacy/image"
import { useFormContext } from "react-hook-form"
import { useState, useRef, useEffect } from "react"
import { ErrorMessage } from "../Form"
import { shuffleArray } from "@/lib/helpers/utils"
import CheckmarkRounded from "@/components/common/icons/checkmark-rounded"
import Remove from "@/components/common/icons/remove"
import Lightbulb from "@/components/common/icons/lightbulb"
import CheckmarkCircle from "@/common/partials/checkmarkCircle"
import { useMediaPredicate } from "react-media-hook"
import classNames from "classnames"
import styles from "../Form.module.css"
import Spinner from "@/components/common/logos/spinner"

export default function Quiz({ form, field, isArticle }) {
  const { register, formState, watch } = useFormContext()
  const [fieldChoices, setFieldChoices] = useState([])
  const { errors } = formState
  const [hasMounted, setMounted] = useState(false)
  const isNarrow = useMediaPredicate("(max-width: 1024px)")
  // This is currently not in use, but we might want to being it back
  const showAlphabet = form?.showAlphabet ?? false

  // This is used due to isMobile not working before mount
  useEffect(() => {
    let isMounted = true
    if (isMounted) {
      setMounted(true)
    }
    return () => {
      isMounted = false
    }
  }, [])

  const radioField = watch(field.id)
  const disableField = Boolean(form?.quiz?.instantFeedback && radioField)

  if (fieldChoices.length === 0) {
    if (field.randomizeChoices) {
      setFieldChoices([...shuffleArray(field.choices)])
    } else {
      setFieldChoices([...field.choices])
    }
  }

  const AnswerIcon = ({ correct }) => {
    if (form?.quiz?.customAnswerIndicator) {
      return (
        <span className="inline-block w-4 h-4 mt-1.5 rounded-full bg-black-100/30"></span>
      )
    }

    if (correct) {
      return (
        <CheckmarkRounded
          className="inline-block w-5 h-5"
          fillColor="#00852E"
        />
      )
    }

    return <Remove className="inline-block w-4 h-4" strokeColor="#AF3E3C" />
  }

  const wrapperClasses = [
    styles.quizWrapper,
    {
      "is-section":
        !isArticle && form?.quiz?.multiplePerPage && form?.quiz?.hasImages
    }
  ]

  const fieldClasses = [
    "w-full mt-4",
    disableField ? "pointer-events-none" : ""
  ]

  return (
    <>
      <div className={classNames(wrapperClasses)}>
        <span className="block text-xs font-medium uppercase mb-4.5">
          Fråga {field.questionIndex} av {form?.quiz?.numberOfQuestions}
        </span>
        <Media
          format={form?.quiz?.imageFormat ?? "4:3"}
          field={field}
          isHidden={!isArticle && !isNarrow}
          isArticle={isArticle}
          hasMounted={hasMounted}
        />
        {field.labelPlacement !== "hidden" && (
          <h2 className="mb-3">
            {field.label}
            {field.isRequired && <span className="ml-1 text-red-100">*</span>}
          </h2>
        )}
        <p className="text-base">{field.description}</p>
        <div className={classNames(fieldClasses)}>
          {fieldChoices.map((choice, index) => {
            let alphabet = String.fromCharCode(96 + 1 + index)
            alphabet = alphabet && alphabet.toUpperCase()

            const selected = radioField === choice.value

            return (
              <label
                className={classNames({
                  selected: selected,
                  "is-incorrect": selected && !choice.gquizIsCorrect
                })}
                key={`${field.id}_${index}`}>
                <input
                  id={`${field.id}_${index}`}
                  name={field.id}
                  type="radio"
                  className="hidden"
                  aria-invalid={errors[field.id] ? "true" : "false"}
                  value={choice.value}
                  {...register(field.id, {
                    required: {
                      value: field.isRequired,
                      message:
                        field.errorMessage ?? "Detta fält är obligatoriskt"
                    }
                  })}
                />
                <div
                  suppressHydrationWarning
                  className="flex items-center text-lg font-bold">
                  <CheckmarkCircle
                    selected={selected}
                    className={classNames({
                      "is-incorrect": selected && !choice.gquizIsCorrect
                    })}
                  />
                  {showAlphabet && (
                    <span className="pr-1 font-bold">{alphabet}.</span>
                  )}
                  {choice.text}
                  {form?.quiz?.instantFeedback &&
                    radioField === choice.value && (
                      <div
                        className={classNames("answer-icon", {
                          "is-correct": choice.gquizIsCorrect
                        })}>
                        <AnswerIcon correct={choice.gquizIsCorrect} />
                      </div>
                    )}
                  {form?.quiz?.instantFeedback &&
                    radioField &&
                    radioField !== choice.value &&
                    choice.gquizIsCorrect && (
                      <div
                        className={classNames("answer-icon", {
                          "is-correct": choice.gquizIsCorrect
                        })}>
                        <AnswerIcon correct={choice.gquizIsCorrect} />
                      </div>
                    )}
                </div>
              </label>
            )
          })}
        </div>
        {form?.quiz?.instantFeedback && field.showExplanation && radioField && (
          <p
            className="explanation"
            dangerouslySetInnerHTML={{ __html: field.explanation }}></p>
        )}
        <Hint hint={field?.hintData} isNarrow={isNarrow} />
        {errors[field.id] && (
          <ErrorMessage>{errors[field.id].message}</ErrorMessage>
        )}
      </div>
      {form?.quiz?.hasImages && form?.quiz?.multiplePerPage && (
        <div className={classNames(wrapperClasses)}>
          <Media
            format={form?.quiz?.imageFormat ?? "4:3"}
            field={field}
            isHidden={!isArticle && isNarrow}
            isArticle={isArticle}
            hasMounted={hasMounted}
          />
        </div>
      )}
    </>
  )
}

const Media = ({ format, field, isHidden, isArticle, hasMounted }) => {
  if (!hasMounted || isHidden) {
    return null
  }

  if (!field?.imageData?.url) {
    return null
  }

  const classes = [
    styles.contentImage,
    {
      "not-article": !isArticle,
      "format-4-3": format === "4:3",
      "format-16-9": format === "16:9",
      "format-square": format === "square"
    }
  ]

  return (
    <figure className={classNames(classes)} key={field.imageData}>
      <div className="flex items-center justify-center w-full h-full">
        <Spinner className="w-8 h-8 opacity-25" />
      </div>
      <Image
        className="object-cover object-center w-full h-full"
        src={field?.imageData?.url}
        alt={field?.imageData?.alt}
        sizes="(max-width: 768px) 100vw, 50vw"
        layout="fill"
      />
    </figure>
  )
}

const Hint = ({ hint, isNarrow }) => {
  const [isActive, setActive] = useState(false)
  const timer = useRef(null)

  if (!hint || hint.text.length === 0) {
    return null
  }

  const showHint = () => {
    clearTimeout(timer.current)
    setActive(!isActive)
  }

  const fadeOut = () => {
    if (!isActive || isNarrow) {
      return false
    }
    timer.current = window.setTimeout(() => {
      setActive(false)
    }, 1000)
  }

  return (
    <div
      className="hint-wrapper"
      onClick={() => showHint()}
      onMouseLeave={() => fadeOut()}>
      <div className="toggle">
        <Lightbulb className="lightbulb" isActive={isActive} />
        {hint.toggle}
      </div>
      <div className={classNames("chat-bubble", { "is-active": isActive })}>
        <p dangerouslySetInnerHTML={{ __html: hint.text }}></p>
      </div>
    </div>
  )
}
